import * as React from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import bind from 'bind-decorator';
import { FilledInputProps } from '@mui/material/FilledInput';

import { InputDialogBaseButton, InputDialogBaseDefaultButton } from './utils/DialogUtils';
import { InputDialogButton } from './inputDialog/InputDialogButton';
import { PositionFixedDialog } from './PositionFixedDialog';

import { selectInputText } from '@egr/xbox/egr-gui-utils/MiscHelper';

import { ListenableEvent } from '@egr/wcf/modules/utils/ListenableEvent';
import { isString } from '@egr/wcf/modules/utils/string';

export interface ModalInputDialogConfig {
    title?: React.ReactNode;
    subTitle?: Array<React.ReactNode>;
    initialValue?: string | number;
    maxLength?: number;
    elementBelowText?: React.ReactNode;
    elementAboveText?: React.ReactNode;
    label?: string;
    multiline?: boolean;
    endAdornment?: React.ReactNode;
    autoFocus?: boolean;
    inputType?: React.InputHTMLAttributes<unknown>['type'];

    requireValidation?: ListenableEvent<void, undefined>;
    validate?: (testValue: string | number | boolean | undefined) => boolean;
    helperText?: (value: string | number | boolean | undefined, valid: boolean) => React.ReactNode;

    max?: number;
    min?: number;
}

export interface InputDialogConfig extends ModalInputDialogConfig {
    buttons: Array<InputDialogBaseButton>;
}

export interface InputDialogBaseProps extends InputDialogConfig {
    open: boolean;
    placeholder?: string;
    selectOnFocus?: boolean;
    close?: () => void;
    onBackdropClick?: VoidFunction;
    noInput?: boolean;
}

interface InputDialogBaseState {
    value: string | number | boolean | undefined;
    valid: boolean;
}

export class InputDialogBase<Props extends InputDialogBaseProps = InputDialogBaseProps> extends React.Component<Props, InputDialogBaseState> {
    protected readonly html5InputProps: FilledInputProps['inputProps'] = {
        style: {fontSize: 'inherit'},
        autoCapitalize: 'true'
    };

    protected defaultName: string = '';
    protected inputElement: HTMLInputElement | undefined;

    constructor(props: Props) {
        super(props);
        this.onFocus = this.onFocus.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.setTextField = this.setTextField.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onValidationEvent = this.onValidationEvent.bind(this);

        this.state = {
            value: this.props.initialValue || '',
            valid: this.props.validate?.(this.props.initialValue || '') ?? true
        };

        props.requireValidation?.addListener(this.onValidationEvent);
    }

    public override render(): React.ReactNode {
        return (
            <PositionFixedDialog
                maxWidth="xs"
                key="input-dialog"
                open={this.props.open}
                onClose={this.onClose}
                hasInput={!this.props.noInput}
            >
                {this.props.elementAboveText}
                {this.title()}
                <DialogContent style={{paddingTop: 5, marginTop: -5}}>
                    {this.subTitle()}
                    {this.input()}
                    {this.elementBelowText()}
                </DialogContent>
                <DialogActions data-sentrylabel="dialogActions-Buttons">
                    {this.props.buttons.map(this.getButton)}
                </DialogActions>
            </PositionFixedDialog>
        );
    }

    protected onClose(): void {
        if (this.props.onBackdropClick != null) {
            return this.props.onBackdropClick();
        }
        this.props.close?.();
    }

    protected input = (): React.ReactNode => {
        if (this.props.noInput === true) {
            this.inputElement = undefined;
            return null;
        }

        let inputProps: FilledInputProps['inputProps'] = this.html5InputProps;

        if (this.props.maxLength != null) {
            inputProps = {...inputProps, maxLength: this.props.maxLength};
        }

        if (
            inputProps != null &&
            this.props.inputType === 'number'
        ) {
            inputProps.min = this.props.min;
            inputProps.max = this.props.max;
        }

        return (
            <TextField
                data-sentrylabel="dialog-inputBar"
                error={this.state.valid === false}
                fullWidth={true}
                autoFocus={this.props.autoFocus}
                inputRef={this.setTextField}
                onChange={this.handleChange}
                placeholder={this.props.placeholder}
                defaultValue={this.props.initialValue}
                InputProps={{
                    endAdornment: this.props.endAdornment,
                }}
                inputProps={inputProps}
                onKeyDown={this.onKeyDown}
                label={this.props.label}
                variant="outlined"
                onFocus={this.onFocus}
                multiline={this.props.multiline}
                type={this.props.inputType}
                maxRows={5}
                helperText={this.props.helperText?.(this.state.value, this.state.valid)}
            />
        );
    };

    protected elementBelowText = (): React.ReactNode => {
        if (this.props.elementBelowText != null) {
            return this.props.elementBelowText;
        }
        return null;
    };

    /**
     * Pressing Return will call the same callback like clicking the main action button (button most right)
     */
    protected onKeyDown(event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>): void {
        if (event.key !== 'Enter') {
            return;
        }

        if (this.props.multiline) {
            if (isString(this.state.value) && this.state.value.split('\n').length >= 5) {
                event.preventDefault();
            }
            return;
        }

        this.callLastCallback();
    }

    protected handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value: string | undefined = event.target.value;
        this.setState({value: value, valid: this.props.validate?.(value) ?? true});
    };

    @bind
    protected getButton(inputButton: InputDialogBaseButton, index: number): React.ReactNode {
        return (
            <InputDialogButton
                key={`${inputButton.key}-${index}`}
                button={inputButton}
                value={this.state.value}
                valid={this.state.valid === true}
                getInput={this.getTextField}
                close={this.props.close}
            />
        );
    }

    protected onFocus(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        if (this.props.selectOnFocus === true) {
            selectInputText(event.target as HTMLInputElement, true);
        }
    }

    protected setTextField(node: HTMLInputElement | undefined): void {
        this.inputElement = node;
    }

    @bind
    protected getTextField(): HTMLInputElement | undefined {
        return this.inputElement;
    }

    protected subTitle(): React.ReactNode {
        if (this.props.subTitle === undefined) {
            return null;
        }
        return (
            <React.Fragment>
                {this.props.subTitle.map(this.subTitleLine)}
                {this.props.noInput ? null : <br/>}
            </React.Fragment>
        );
    }

    protected subTitleLine(value: React.ReactNode, index: number, stringList: Array<React.ReactNode>): React.ReactNode {
        return (
            <Typography
                key={index}
                variant="body2"
                style={index === stringList.length - 1 ? {marginBottom: 10} : {}}
            >
                {value}
            </Typography>
        );
    }

    protected title(): React.ReactNode {
        if (this.props.title == null || this.props.title === '') {
            return null;
        }
        return (
            <DialogTitle>{this.props.title}</DialogTitle>
        );
    }

    @bind
    protected callLastCallback(): void {
        const lastButton: InputDialogBaseButton | undefined = this.props.buttons[this.props.buttons.length - 1];

        if (!this.isValidDefaultButton(lastButton)) {
            return;
        }

        if (lastButton.callback != null) {
            const inputElement: HTMLInputElement | undefined = this.getTextField();

            if (inputElement != null) {
                lastButton.callback(inputElement.value);
            } else {
                lastButton.callback('');
            }
        }

        if (this.props.close != null && !lastButton.preventCloseCall) {
            this.props.close();
        }
    }

    @bind
    private isValidDefaultButton(button: InputDialogBaseButton | undefined): button is InputDialogBaseDefaultButton {
        return (
            button != null &&
            button.type !== 'link' &&
            (!button.checkInput || (this.state.value != null && this.state.value !== '' && (this.state.valid === true)))
        );
    }

    private onValidationEvent(): void {
        this.setState({valid: this.props.validate?.(this.state.value) ?? true});
    }
}